import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import AppLayout from '@components/layout/appLayout/AppLayout';
import LoadingIndicator from '@components/loadingIndicator/LoadingIndicator';
import { MenuOption, Modules } from '@components/menuBar/MenuBar';
import React, { Suspense, lazy, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { logoutUser, updateUserConfig } from './actions';
import { trackLocationOfInterest } from './helpers/analytics';
import { usePageTracking } from './hooks/usePageTracking';
import { features } from './pages/dashboards/dashboardFramework/helpers/features';
import CreatePassword from './pages/password/CreatePassword';
import { ResetPassword } from './pages/password/ResetPassword';
import SignIn from './pages/signIn/SignIn';
import SignInForm from './pages/signIn/SignInForm';
import SignInMagicLink from './pages/signIn/SignInMagicLink';
import CompleteSignUp from './pages/signup/CompleteSignUp';
import { LEGACY_SIGN_UP_PATH, SIGN_UP_PATH } from './pages/signup/helper';
import LegacyPathRedirect from './routes/LegacyPathRedirect';
import {
  LEGACY_RENT_NAVIGATOR_BASE_PATH,
  RENT_NAVIGATOR_BASE_PATH,
} from './routes/dashboardPaths';

const ChaosAiPanel = lazy(() => import('./pages/ai/ChaosAiPanel'));
const Dashboards = lazy(() => import('./pages/dashboards/Dashboards'));
const loadSignUp = () => import('./pages/signup/SignUp');
const SignUp = lazy(loadSignUp);
const AiReportsPanel = lazy(() => import('./pages/aiReports/AiReportsPanel'));
const PointsPanel = lazy(() => import('./pages/points/PointsPanel'));
const PointsInviteResolveView = lazy(
  () => import('./pages/points/views/PointsInviteResolveView'),
);
const Settings = lazy(() => import('./pages/settings/Settings'));

export const ROUTES_WITH_INSIGHTS = ['assess', 'optimize', 'discover'];
export const ROUTES_WITH_INSIGHTS_FOR_TRIAL_ORGANIZATION = ['optimize'];

interface AppRoutesProps {
  isAuthenticated: boolean;
  user: any;
  logoutUserAction: () => void;
  updateUserConfigAction: (partialUserConfig: object) => void;
  conversationId?: string;
  isSelfSignUp: boolean;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  isAuthenticated,
  user,
  logoutUserAction,
  updateUserConfigAction,
  conversationId,
  isSelfSignUp,
}) => {
  const { i18n } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  // Track page views automatically on route changes
  usePageTracking();

  const hasChaosAiFeature =
    !isSelfSignUp || user?.globalFeatures?.includes(features.CHAOS_AI);

  const hasAiReportsFeature = user?.globalFeatures?.includes(
    features.AI_REPORTS,
  );

  const hasPointsFeature = user?.globalFeatures?.includes(features.POINTS);

  const selectedModule = useMemo(
    () => location?.pathname.split('/')?.[1],
    [location?.pathname],
  );

  const onSelectedMenuChange = useCallback(
    (menuOption: MenuOption) => {
      if (menuOption === Modules.myPlaces) {
        trackLocationOfInterest('open_tab');
      }

      if (menuOption === 'chaos-ai' && conversationId) {
        navigate(`/chaos-ai/${conversationId}`);
      } else {
        navigate(`/${menuOption}`);
      }
    },
    [conversationId, navigate],
  );

  const onLanguageChange = useCallback(
    (languageCode: string) => {
      updateUserConfigAction({ language: languageCode });
    },
    [updateUserConfigAction],
  );

  useEffect(() => {
    if (user?.info?.settings?.language) {
      i18n.changeLanguage(user?.info?.settings?.language);
    }
  }, [i18n, user?.info?.settings?.language]);

  // Preload the sign-up page on the login page, so "Sign up for free"
  // opens it straight away instead of showing a loader.
  useEffect(() => {
    if (!isAuthenticated) {
      loadSignUp().catch(() => {
        // Chunk failed to preload - it is loaded again on navigation
      });
    }
  }, [isAuthenticated]);

  const renderUnauthorizedRoutes = useCallback(
    () => (
      <Suspense
        fallback={
          <CenterPanel>
            <LoadingIndicator />
          </CenterPanel>
        }
      >
        <Routes>
          <Route path="/*" element={<SignInForm />} />
          <Route path="signup" element={<Navigate to={SIGN_UP_PATH} />} />
          {/* TODO: This can be removed after 2-week release for self payment integration */}
          {/* Old sign-up URL, still used by sent verification emails and external links */}
          <Route
            path={`${LEGACY_SIGN_UP_PATH}/*`}
            element={
              <LegacyPathRedirect
                fromBasePath={LEGACY_SIGN_UP_PATH}
                toBasePath={SIGN_UP_PATH}
              />
            }
          />
          <Route path="signup/:featureType/*" Component={SignUp} />
          <Route
            path="signup/:featureType/verify/:signUpType/:token"
            Component={CompleteSignUp}
          />

          <Route path="sign-in" Component={SignIn} />
          <Route path="sign-in-link" Component={SignInMagicLink} />
          <Route path="create-password" Component={CreatePassword} />
          <Route path="password-recovery" Component={ResetPassword} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    ),
    [],
  );

  const insightRoutes = useMemo(() => {
    if (user?.info?.isSelfSignUp) {
      return ROUTES_WITH_INSIGHTS_FOR_TRIAL_ORGANIZATION;
    }

    return ROUTES_WITH_INSIGHTS;
  }, [user?.info?.isSelfSignUp]);

  const renderAuthenticatedRoutes = useCallback(
    () => (
      <AppLayout
        selectedMenu={selectedModule}
        user={user?.info}
        onSelectedMenuChange={onSelectedMenuChange}
        onLogoutClick={logoutUserAction}
        onLanguageChange={onLanguageChange}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <Routes>
            <Route
              key="legacy-rent-navigator"
              path={`${LEGACY_RENT_NAVIGATOR_BASE_PATH}/*`}
              element={
                <LegacyPathRedirect
                  fromBasePath={LEGACY_RENT_NAVIGATOR_BASE_PATH}
                  toBasePath={RENT_NAVIGATOR_BASE_PATH}
                />
              }
            />
            {insightRoutes.map((module) => (
              <Route
                key={`module-${module}`}
                path={`/${module}/*`}
                Component={Dashboards}
              />
            ))}
            {hasChaosAiFeature && (
              <Route
                key="chaos-ai"
                path="/chaos-ai/*"
                Component={ChaosAiPanel}
              />
            )}
            {hasAiReportsFeature && (
              <Route
                key="ai-reports"
                path="/ai-reports/*"
                Component={AiReportsPanel}
              />
            )}
            <Route
              key="my-places-invite"
              path="/my-places/invite/:link"
              Component={PointsInviteResolveView}
            />
            {hasPointsFeature && (
              <Route
                key="my-places"
                path="/my-places/*"
                Component={PointsPanel}
              />
            )}
            <Route key="settings" path="/settings" Component={Settings} />
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    hasChaosAiFeature
                      ? `/${Modules.chaosAi}`
                      : RENT_NAVIGATOR_BASE_PATH
                  }
                  replace
                />
              }
            />
          </Routes>
        </Suspense>
      </AppLayout>
    ),
    [
      selectedModule,
      user?.info,
      onSelectedMenuChange,
      logoutUserAction,
      onLanguageChange,
      insightRoutes,
      hasChaosAiFeature,
      hasAiReportsFeature,
      hasPointsFeature,
    ],
  );

  return isAuthenticated
    ? renderAuthenticatedRoutes()
    : renderUnauthorizedRoutes();
};

const mapStateToProps = (state: any) => ({
  isAuthenticated: state.auth.authenticated,
  user: state?.user || {},
  conversationId: state?.ai?.conversationId,
  isSelfSignUp: state?.user?.info?.isSelfSignUp,
});

export default connect(mapStateToProps, {
  logoutUserAction: logoutUser,
  updateUserConfigAction: updateUserConfig,
})(AppRoutes);
